import React, { memo, useCallback, useMemo, useState } from 'react';
import { Platform, LayoutChangeEvent } from 'react-native';
import { Box, Text, useSafeAreaInsets } from '@betty/moonlight';
import BackButton from '../components/BackButton/BackButton';
import { CustomHeaderProps } from './Header.types';
import { getNavigationDepth } from '../utils';
import CloseButton from '../components/CloseButton/CloseButton';
import { useTranslation } from '@betty/i18n';

function Header({ navigation, options, headerRight }: CustomHeaderProps) {
  const { left } = useSafeAreaInsets();
  const [leftWidth, setLeftWidth] = useState(0);
  const [rightWidth, setRightWidth] = useState(0);
  const { t } = useTranslation('common');
  const isModalScreen = options.presentation === 'transparentModal';
  const { showBackIcon, showCloseIcon } = useMemo(
    () => ({
      showBackIcon: !isModalScreen && navigation.getState().index > 0,
      showCloseIcon:
        isModalScreen ||
        Platform.OS === 'web' ||
        (getNavigationDepth(navigation) > 1 && navigation.canGoBack()),
    }),
    [isModalScreen, navigation],
  );

  const onLeftLayout = useCallback((e: LayoutChangeEvent) => {
    setLeftWidth(e.nativeEvent.layout.width);
  }, []);

  const onRightLayout = useCallback((e: LayoutChangeEvent) => {
    setRightWidth(e.nativeEvent.layout.width);
  }, []);

  return (
    <Box
      flexDirection="row"
      paddingTop={Platform.select({
        ios: 'space-0',
        web: 'space-0',
        android: 'safe-area-inset-top',
      })}
    >
      <Box width={left} />
      <Box flex={1}>
        <Box height="safe-area-inset-top-pure" />
        <Box
          position="relative"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          gap="space-100"
          paddingHorizontal="space-100"
        >
          <Box
            absoluteFill
            centerContent
            paddingLeft={leftWidth}
            paddingRight={rightWidth}
          >
            <Text variant="body-m" gradient="darkSurfaceAccent">
              {options.title}
            </Text>
          </Box>
          <Box
            onLayout={onLeftLayout}
            flexDirection="row"
            alignItems="center"
            gap="space-100"
          >
            {showBackIcon ? <BackButton navigation={navigation} /> : null}
            {showBackIcon && options.headerBackTitle && (
              <Text variant="h6" gradient="darkSurfaceAccent">
                {t(options.headerBackTitle)}
              </Text>
            )}
          </Box>
          <Box
            flexDirection="row"
            alignItems="center"
            gap="space-100"
            onLayout={onRightLayout}
          >
            {headerRight ?? null}
            {showCloseIcon ? <CloseButton navigation={navigation} /> : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export const HEADER_HEIGHT = 32;

export default memo(Header);
